import { isUnauthorized, type RouterOutputs } from "@onadssuper/api-client";
import { ROLE_LABELS, personName } from "@onadssuper/validators";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { MailWarning } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Button, EmptyState, Field, FormError, Input, PageSpinner } from "../../components/ui";
import { AuthLayout } from "../../features/auth/auth-layout";
import { useSignOut } from "../../features/auth/use-sign-out";
import { INVITATION_TOKEN_HEADER, authClient } from "../../lib/auth-client";
import { markSignedIn } from "../../lib/session";
import { useTRPC } from "../../lib/trpc";

export const Route = createFileRoute("/invite/$token")({ component: InvitePage });

type Preview = RouterOutputs["invitations"]["preview"];
const MIN_PASSWORD = 12;

function InvitePage() {
  const { token } = Route.useParams();
  const trpc = useTRPC();
  // A POST on purpose: the token travels in the body, never in a request URL (SEC-09).
  const preview = useMutation(trpc.invitations.preview.mutationOptions({ meta: { silent: true } }));
  const me = useQuery({ ...trpc.me.get.queryOptions(), retry: false });
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    preview.mutate({ token });
  }, [preview, token]);

  if (preview.isError) {
    return (
      <AuthLayout
        title="Invitation not available"
        footer={<Link to="/sign-in">Go to sign in</Link>}
      >
        <EmptyState
          icon={<MailWarning className="size-8" />}
          title="This invitation link is invalid or has expired"
          description="Invitation links work once and expire after 7 days. Ask the person who invited you to send a new one."
        />
      </AuthLayout>
    );
  }
  if (!preview.data || me.isPending) return <PageSpinner />;

  const invitation = preview.data;
  const signedIn = me.isSuccess;
  if (me.isError && !isUnauthorized(me.error)) return <PageSpinner />;

  return (
    <AuthLayout
      title={`Join ${invitation.workspaceName}`}
      description={
        <>
          You've been invited as{" "}
          <strong className="text-text">{ROLE_LABELS[invitation.role]}</strong> ({invitation.email}
          ).
        </>
      }
    >
      {signedIn ? (
        me.data.user.email.toLowerCase() === invitation.email.toLowerCase() ? (
          <AcceptInvitation token={token} />
        ) : (
          <WrongAccount signedInAs={me.data.user.email} invitedEmail={invitation.email} />
        )
      ) : invitation.hasAccount ? (
        <div className="flex flex-col gap-3 text-sm text-muted">
          <p>You already have an account. Sign in as {invitation.email} to accept.</p>
          <Button asChild>
            <Link to="/sign-in" search={{ redirect: `/invite/${token}` }}>
              Sign in to accept
            </Link>
          </Button>
        </div>
      ) : (
        <SignUpAndAccept token={token} invitation={invitation} />
      )}
    </AuthLayout>
  );
}

const useAccept = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation(
    trpc.invitations.accept.mutationOptions({
      onSuccess: async (result) => {
        markSignedIn();
        await queryClient.invalidateQueries({ queryKey: trpc.me.get.queryKey() });
        await navigate({ to: "/w/$slug", params: { slug: result.workspaceSlug } });
      },
      meta: { silent: false },
    }),
  );
};

function AcceptInvitation({ token }: { token: string }) {
  const accept = useAccept();
  return (
    <div className="flex flex-col gap-3">
      <FormError message={accept.error?.message} />
      <Button loading={accept.isPending} onClick={() => accept.mutate({ token })}>
        Accept invitation
      </Button>
    </div>
  );
}

function WrongAccount({ signedInAs, invitedEmail }: { signedInAs: string; invitedEmail: string }) {
  const signOut = useSignOut();
  return (
    <div className="flex flex-col gap-3 text-sm text-muted">
      <p>
        You're signed in as <strong className="text-text">{signedInAs}</strong>, but this invitation
        was sent to <strong className="text-text">{invitedEmail}</strong>. Sign out and use that
        address to accept it.
      </p>
      <Button variant="secondary" onClick={() => void signOut()}>
        Sign out
      </Button>
    </div>
  );
}

function SignUpAndAccept({ token, invitation }: { token: string; invitation: Preview }) {
  const accept = useAccept();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<{ name?: string; password?: string; form?: string }>({});
  const [pending, setPending] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    const parsedName = personName.safeParse(name);
    if (!parsedName.success) next.name = "Enter your name (up to 100 characters).";
    if (password.length < MIN_PASSWORD) next.password = `Use at least ${MIN_PASSWORD} characters.`;
    else if (password !== confirm) next.password = "The passwords don't match.";
    setErrors(next);
    if (next.name || next.password || !parsedName.success) return;

    setPending(true);
    const { error } = await authClient.signUp.email(
      { email: invitation.email, password, name: parsedName.data },
      // The server only creates accounts for a valid invitation to this exact email.
      { headers: { [INVITATION_TOKEN_HEADER]: token } },
    );
    setPending(false);
    if (error) {
      setErrors({
        form:
          error.status === 429
            ? "Too many attempts. Try again in a minute."
            : (error.message ?? "We couldn't create your account. The link may have expired."),
      });
      return;
    }
    accept.mutate({ token });
  };

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4" noValidate>
      <Field label="Email">
        <Input type="email" value={invitation.email} readOnly disabled />
      </Field>
      <Field label="Your name" error={errors.name}>
        <Input
          autoComplete="name"
          maxLength={100}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field
        label="Password"
        error={errors.password}
        hint={`At least ${MIN_PASSWORD} characters. Passwords found in known breaches are refused.`}
      >
        <Input
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>
      <Field label="Confirm password">
        <Input
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
      </Field>
      <FormError message={errors.form ?? accept.error?.message} />
      <Button type="submit" loading={pending || accept.isPending}>
        Create account and join
      </Button>
    </form>
  );
}
